import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useFormat } from '@nucleus-commerce/i18n/react';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { computeChange } from '../schemas/report-change';

export interface ChangeIndicatorProps {
  current: number | null;
  previous: number | null;
  /** Response times and missed chats: down is green and up is red. */
  lowerIsBetter?: boolean;
}

/** Up is green and down is red, unless the figure is one where less is better. */
export function ChangeIndicator({
  current,
  previous,
  lowerIsBetter = false,
}: ChangeIndicatorProps) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const change =
    current === null || previous === null
      ? { kind: 'none' as const }
      : computeChange(current, previous);

  if (change.kind === 'none') {
    return <span className="text-muted-foreground">{t('change.none')}</span>;
  }
  if (change.kind === 'flat') {
    return (
      <span className="text-muted-foreground inline-flex items-center gap-1">
        <Minus className="size-3" aria-hidden />
        {t('change.flat')}
      </span>
    );
  }
  const Icon = change.kind === 'up' ? TrendingUp : TrendingDown;
  const good = (change.kind === 'up') !== lowerIsBetter;
  return (
    <span
      className={cn('inline-flex items-center gap-1', good ? 'text-success' : 'text-destructive')}
    >
      <Icon className="size-3" aria-hidden />
      {t(`change.${change.kind}`, { percent: format.percent(change.ratio) })}
    </span>
  );
}
